
import React from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { ChevronUp } from 'lucide-react';

interface WhyKIVideosProps {
  industry?: string;
}

const WhyKIVideos: React.FC<WhyKIVideosProps> = ({ industry }) => {
  const { currentLanguage } = useLanguage();
  
  // Industry-specific content could be added here
  
  return (
    <section className="py-16 bg-gradient-to-b from-[#0B0B12] to-[#070709] relative overflow-hidden">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {currentLanguage === 'de' ? 'KI Social Media Videos: 300% mehr Engagement & 70% Kostenersparnis' :
             currentLanguage === 'en' ? 'AI Social Media Videos: 300% More Engagement & 70% Cost Savings' :
             currentLanguage === 'fr' ? 'Vidéos IA pour Médias Sociaux: 300% Plus d\'Engagement & 70% d\'Économies' :
             'Video AI per Social Media: 300% in Più di Engagement & 70% di Risparmio sui Costi'}
          </h2>
          <div className="w-20 h-1 bg-purple-600 mx-auto mb-6"></div>
          <p className="text-gray-300 max-w-3xl mx-auto">
            {currentLanguage === 'de' ? 'Die herkömmliche Videoproduktion ist zeitaufwendig, teuer und unflexibel. KI-generierte Videos liefern überlegene Performance zu einem Bruchteil der Kosten.' :
             currentLanguage === 'en' ? 'Traditional video production is time-consuming, expensive, and inflexible. AI-generated videos deliver superior performance at a fraction of the cost.' :
             currentLanguage === 'fr' ? 'La production vidéo traditionnelle prend du temps, coûte cher et manque de flexibilité. Les vidéos générées par IA offrent des performances supérieures à une fraction du coût.' :
             'La produzione video tradizionale richiede tempo, è costosa e inflessibile. I video generati dall\'IA offrono prestazioni superiori a una frazione del costo.'}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          {/* Left Column - Pain Points */}
          <div className="bg-gradient-to-br from-[#14141F]/60 to-[#0D0D16]/60 backdrop-blur-sm p-8 rounded-xl border border-red-500/20 hover:border-red-500/40 transition-all duration-300">
            <h3 className="text-2xl font-semibold text-white mb-6 flex items-center">
              <span className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center mr-3">
                <ChevronUp className="w-5 h-5 text-red-400 transform rotate-180" />
              </span>
              {currentLanguage === 'de' ? 'Herausforderungen traditioneller Videos' :
               currentLanguage === 'en' ? 'Traditional Video Challenges' :
               currentLanguage === 'fr' ? 'Défis des Vidéos Traditionnelles' :
               'Sfide dei Video Tradizionali'}
            </h3>
            <ul className="space-y-4">
              {[
                {
                  title: currentLanguage === 'de' ? 'Hohe Kosten' : 
                         currentLanguage === 'en' ? 'High Costs' : 
                         currentLanguage === 'fr' ? 'Coûts Élevés' : 
                         'Costi Elevati',
                  desc: currentLanguage === 'de' ? 'Teure Ausrüstung, Crew, Locations und Post-Produktion lassen die Kosten explodieren.' : 
                        currentLanguage === 'en' ? 'Expensive equipment, crew, locations, and post-production cause costs to skyrocket.' : 
                        currentLanguage === 'fr' ? 'Équipement coûteux, équipe, lieux de tournage et post-production font exploser les coûts.' : 
                        'Attrezzature costose, troupe, location e post-produzione fanno lievitare i costi.'
                },
                {
                  title: currentLanguage === 'de' ? 'Zeitaufwand' : 
                         currentLanguage === 'en' ? 'Time-Consuming' : 
                         currentLanguage === 'fr' ? 'Chronophage' : 
                         'Dispendio di Tempo',
                  desc: currentLanguage === 'de' ? 'Wochen oder Monate von der Konzeption bis zum fertigen Video – zu langsam im digitalen Zeitalter.' : 
                        currentLanguage === 'en' ? 'Weeks or months from concept to finished video – too slow in the digital age.' : 
                        currentLanguage === 'fr' ? 'Des semaines ou des mois entre le concept et la vidéo finale – trop lent à l\'ère numérique.' : 
                        'Settimane o mesi dal concept al video finale – troppo lento nell\'era digitale.'
                },
                {
                  title: currentLanguage === 'de' ? 'Begrenzte Skalierbarkeit' : 
                         currentLanguage === 'en' ? 'Limited Scalability' : 
                         currentLanguage === 'fr' ? 'Scalabilité Limitée' : 
                         'Scalabilità Limitata',
                  desc: currentLanguage === 'de' ? 'Mehrere Video-Varianten für A/B Tests oder Personalisierung sind praktisch unmöglich.' : 
                        currentLanguage === 'en' ? 'Multiple video variations for A/B testing or personalization are virtually impossible.' : 
                        currentLanguage === 'fr' ? 'Plusieurs variations vidéo pour les tests A/B ou la personnalisation sont pratiquement impossibles.' : 
                        'Più varianti video per test A/B o personalizzazione sono praticamente impossibili.'
                },
                {
                  title: currentLanguage === 'de' ? 'Mangelnde Flexibilität' : 
                         currentLanguage === 'en' ? 'Lack of Flexibility' : 
                         currentLanguage === 'fr' ? 'Manque de Flexibilité' : 
                         'Mancanza di Flessibilità',
                  desc: currentLanguage === 'de' ? 'Änderungen nach der Produktion sind aufwändig und teuer oder sogar unmöglich.' : 
                        currentLanguage === 'en' ? 'Changes after production are laborious, expensive, or even impossible.' : 
                        currentLanguage === 'fr' ? 'Les modifications après production sont laborieuses, coûteuses, voire impossibles.' : 
                        'Modifiche dopo la produzione sono laboriose, costose o addirittura impossibili.'
                }
              ].map((item, idx) => (
                <li key={idx} className="flex items-start">
                  <span className="w-6 h-6 rounded-full bg-red-500/20 flex items-center justify-center mt-1 mr-3 flex-shrink-0">
                    <span className="w-2 h-2 bg-red-400 rounded-full"></span>
                  </span>
                  <div>
                    <h4 className="text-white font-medium mb-1">{item.title}</h4>
                    <p className="text-gray-400 text-sm">{item.desc}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Right Column - Solution */}
          <div className="bg-gradient-to-br from-[#14141F]/60 to-[#0D0D16]/60 backdrop-blur-sm p-8 rounded-xl border border-purple-500/20 hover:border-purple-500/40 transition-all duration-300">
            <h3 className="text-2xl font-semibold text-white mb-6 flex items-center">
              <span className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center mr-3">
                <ChevronUp className="w-5 h-5 text-purple-400" />
              </span>
              {currentLanguage === 'de' ? 'Der KI-Video Vorteil' :
               currentLanguage === 'en' ? 'The AI Video Advantage' :
               currentLanguage === 'fr' ? 'L\'Avantage des Vidéos IA' :
               'Il Vantaggio dei Video AI'}
            </h3>
            <ul className="space-y-4">
              {[
                {
                  title: currentLanguage === 'de' ? 'Bis zu 70% Kostenreduktion' : 
                         currentLanguage === 'en' ? 'Up to 70% Cost Reduction' : 
                         currentLanguage === 'fr' ? 'Jusqu\'à 70% de Réduction des Coûts' : 
                         'Fino al 70% di Riduzione dei Costi',
                  desc: currentLanguage === 'de' ? 'Keine teuren Filmteams, kein Equipment, keine Logistikkosten – nur effiziente KI-gestützte Produktion.' : 
                        currentLanguage === 'en' ? 'No expensive film crews, equipment, or logistics costs – just efficient AI-powered production.' : 
                        currentLanguage === 'fr' ? 'Pas d\'équipes de tournage coûteuses, d\'équipement ou de coûts logistiques – juste une production efficace alimentée par l\'IA.' : 
                        'Nessuna costosa troupe cinematografica, attrezzatura o costi logistici - solo produzione efficiente alimentata dall\'IA.'
                },
                {
                  title: currentLanguage === 'de' ? 'Produktion in Stunden statt Wochen' : 
                         currentLanguage === 'en' ? 'Production in Hours, Not Weeks' : 
                         currentLanguage === 'fr' ? 'Production en Heures, Pas en Semaines' : 
                         'Produzione in Ore, Non in Settimane',
                  desc: currentLanguage === 'de' ? 'Schneller Time-to-Market für Ihre Kampagnen mit ersten Ergebnissen in 24-48 Stunden.' : 
                        currentLanguage === 'en' ? 'Faster time-to-market for your campaigns with initial results in 24-48 hours.' : 
                        currentLanguage === 'fr' ? 'Mise sur le marché plus rapide pour vos campagnes avec des résultats initiaux en 24 à 48 heures.' : 
                        'Time-to-market più veloce per le tue campagne con risultati iniziali in 24-48 ore.'
                },
                {
                  title: currentLanguage === 'de' ? 'Unbegrenzte Varianten und Personalisierung' : 
                         currentLanguage === 'en' ? 'Unlimited Variants and Personalization' : 
                         currentLanguage === 'fr' ? 'Variantes et Personnalisation Illimitées' : 
                         'Varianti e Personalizzazione Illimitate',
                  desc: currentLanguage === 'de' ? 'Erstellen Sie dutzende Varianten für A/B Tests, verschiedene Zielgruppen oder Plattformen.' : 
                        currentLanguage === 'en' ? 'Create dozens of variants for A/B testing, different audiences, or platforms.' : 
                        currentLanguage === 'fr' ? 'Créez des dizaines de variantes pour les tests A/B, différents publics ou plateformes.' : 
                        'Crea dozzine di varianti per test A/B, diversi pubblici o piattaforme.'
                },
                {
                  title: currentLanguage === 'de' ? 'Datengestützte Performance' : 
                         currentLanguage === 'en' ? 'Data-Driven Performance' : 
                         currentLanguage === 'fr' ? 'Performance Basée sur les Données' : 
                         'Prestazioni Basate sui Dati',
                  desc: currentLanguage === 'de' ? 'KI-Videos basieren auf Analysen erfolgreicher Formate und werden ständig für bessere Ergebnisse optimiert.' : 
                        currentLanguage === 'en' ? 'AI videos are based on analyses of successful formats and are constantly optimized for better results.' : 
                        currentLanguage === 'fr' ? 'Les vidéos IA sont basées sur des analyses de formats réussis et sont constamment optimisées pour de meilleurs résultats.' : 
                        'I video AI si basano su analisi di formati di successo e vengono costantemente ottimizzati per risultati migliori.'
                }
              ].map((item, idx) => (
                <li key={idx} className="flex items-start">
                  <span className="w-6 h-6 rounded-full bg-purple-500/20 flex items-center justify-center mt-1 mr-3 flex-shrink-0">
                    <span className="w-2 h-2 bg-purple-400 rounded-full"></span>
                  </span>
                  <div>
                    <h4 className="text-white font-medium mb-1">{item.title}</h4>
                    <p className="text-gray-400 text-sm">{item.desc}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Key Points in Grid Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            {
              icon: "team",
              title: currentLanguage === 'de' ? 'Expertenteam' : 
                     currentLanguage === 'en' ? 'Expert Team' : 
                     currentLanguage === 'fr' ? 'Équipe d\'Experts' : 
                     'Team di Esperti',
              desc: currentLanguage === 'de' ? 'Expertenteam mit fortschrittlichen KI-Tools für Instagram, TikTok & Facebook Videos' : 
                    currentLanguage === 'en' ? 'Expert team with advanced AI tools for Instagram, TikTok & Facebook videos' : 
                    currentLanguage === 'fr' ? 'Équipe d\'experts avec outils IA avancés pour vidéos Instagram, TikTok et Facebook' : 
                    'Team di esperti con strumenti AI avanzati per video Instagram, TikTok e Facebook'
            },
            {
              icon: "pros",
              title: currentLanguage === 'de' ? 'Professionelle Editoren' : 
                     currentLanguage === 'en' ? 'Professional Editors' : 
                     currentLanguage === 'fr' ? 'Éditeurs Professionnels' : 
                     'Editor Professionisti',
              desc: currentLanguage === 'de' ? 'Team aus ausschließlich professionellen Video-Editoren mit langjähriger Erfahrung' : 
                    currentLanguage === 'en' ? 'Team of exclusively professional video editors with years of experience' : 
                    currentLanguage === 'fr' ? 'Équipe d\'éditeurs vidéo exclusivement professionnels avec des années d\'expérience' : 
                    'Team di editor video esclusivamente professionisti con anni di esperienza'
            },
            {
              icon: "focus",
              title: currentLanguage === 'de' ? 'Fokus auf Ihren Erfolg' : 
                     currentLanguage === 'en' ? 'Focus on Your Success' : 
                     currentLanguage === 'fr' ? 'Concentration sur Votre Succès' : 
                     'Focus sul Tuo Successo',
              desc: currentLanguage === 'de' ? 'Vollständige Fokussierung auf Ihren Erfolg durch maßgeschneiderte KI-gestützte Videostrategien' : 
                    currentLanguage === 'en' ? 'Complete focus on your success through customized AI-powered video strategies' : 
                    currentLanguage === 'fr' ? 'Concentration totale sur votre succès grâce à des stratégies vidéo personnalisées alimentées par l\'IA' : 
                    'Concentrazione totale sul tuo successo attraverso strategie video personalizzate alimentate dall\'IA'
            },
            {
              icon: "expertise",
              title: currentLanguage === 'de' ? 'Format-Expertise' : 
                     currentLanguage === 'en' ? 'Format Expertise' : 
                     currentLanguage === 'fr' ? 'Expertise en Format' : 
                     'Competenza di Formato',
              desc: currentLanguage === 'de' ? 'Professionelles Team mit Expertise in allen Social Media Formaten (Instagram Reels, TikTok)' : 
                    currentLanguage === 'en' ? 'Professional team with expertise in all social media formats (Instagram Reels, TikTok)' : 
                    currentLanguage === 'fr' ? 'Équipe professionnelle avec expertise dans tous les formats de médias sociaux (Instagram Reels, TikTok)' : 
                    'Team professionale con competenza in tutti i formati di social media (Instagram Reels, TikTok)'
            }
          ].map((item, idx) => (
            <div key={idx} className="bg-gradient-to-br from-[#14141F]/60 to-[#0D0D16]/60 backdrop-blur-sm p-5 rounded-xl border border-purple-900/20 hover:border-purple-500/40 transition-all duration-300 group">
              <div className="w-12 h-12 bg-purple-900/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-purple-900/40 transition-all">
                {item.icon === "team" && (
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                )}
                {item.icon === "pros" && (
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z" />
                  </svg>
                )}
                {item.icon === "focus" && (
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                )}
                {item.icon === "expertise" && (
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                )}
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
              <p className="text-gray-400 text-sm">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
      
      {/* SEO-Optimized Hidden Section for Keywords */}
      <div className="sr-only" aria-hidden="true">
        <h2>KI Social Media Videos Vorteile</h2>
        <p>Instagram Reels mit künstlicher Intelligenz, Facebook Ads KI-optimiert, TikTok Videos mit AI, LinkedIn KI-Marketing</p>
      </div>
    </section>
  );
};

export default WhyKIVideos;
